import { useEffect, useState } from "react";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { BookOpen, ExternalLink, AlertTriangle } from "lucide-react";
import { apiJson } from "@/lib/api";

interface Source { id: string; nom: string; url_source: string; verifie_le: string }
interface Etape { n: number; titre: string; contenu: string; regles: string[]; sources_detail: Source[] }
interface Playbook { id: string; titre: string; resume: string; public: string; version: string; verifie_le: string; statut: string; etapes: Etape[]; pieges: string[]; a_valider: string[] }
interface Meta { id: string; titre: string; discipline: string; resume: string }

const fmt = (d: string) => new Date(d).toLocaleDateString("fr-FR");

export default function SavoirFaire() {
  const [metas, setMetas] = useState<Meta[]>([]);
  const [pb, setPb] = useState<Playbook | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    apiJson<{ playbooks: Meta[] }>("/api/brain/playbooks").then(async (r) => {
      setMetas(r.playbooks);
      if (r.playbooks[0]) setPb(await apiJson<Playbook>(`/api/brain/playbooks/${r.playbooks[0].id}`));
    }).catch(() => setError(true));
  }, []);

  const open = async (id: string) => setPb(await apiJson<Playbook>(`/api/brain/playbooks/${id}`));

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <DashboardSidebar />
        <main className="flex-1 overflow-y-auto">
          <header className="h-14 flex items-center gap-3 border-b border-border px-4">
            <SidebarTrigger />
            <BookOpen className="h-5 w-5 text-primary" />
            <h1 className="text-lg font-semibold">Savoir-faire de Grafton</h1>
          </header>
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 space-y-5">
            <p className="text-sm text-muted-foreground">Les méthodes que Grafton applique à votre situation. Chaque étape cite ses sources officielles ; une méthode est marquée « à valider » tant qu'un expert ne l'a pas relue.</p>
            {error && <p className="text-sm text-destructive">Bibliothèque momentanément indisponible.</p>}
            {metas.length > 1 && (
              <div className="flex flex-wrap gap-2">{metas.map((m) => <Badge key={m.id} variant="outline" className="cursor-pointer" onClick={() => open(m.id)}>{m.titre}</Badge>)}</div>
            )}
            {pb && (
              <>
                <Card>
                  <CardHeader>
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <Badge variant="outline" className="text-[10px] capitalize">Financement</Badge>
                      <Badge variant="outline" className="text-[10px]">{pb.version} · vérifié le {fmt(pb.verifie_le)}</Badge>
                    </div>
                    <CardTitle className="text-xl">{pb.titre}</CardTitle>
                    <CardDescription>{pb.resume}</CardDescription>
                    <p className="text-xs text-amber-500 mt-2">{pb.statut}</p>
                  </CardHeader>
                </Card>
                {pb.etapes.map((e) => (
                  <Card key={e.n}>
                    <CardContent className="p-5 space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="h-7 w-7 shrink-0 rounded-full bg-primary text-primary-foreground text-sm font-semibold flex items-center justify-center">{e.n}</div>
                        <div className="space-y-2">
                          <h2 className="font-semibold">{e.titre}</h2>
                          <p className="text-sm text-muted-foreground leading-relaxed">{e.contenu}</p>
                          {e.regles.length > 0 && <ul className="text-sm space-y-1 list-disc pl-5">{e.regles.map((r, i) => <li key={i}>{r}</li>)}</ul>}
                          {e.sources_detail.length > 0 && (
                            <div className="flex flex-wrap gap-2 pt-1">
                              {e.sources_detail.map((s) => (
                                <a key={s.id} href={s.url_source} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline"><ExternalLink className="h-3 w-3" />{s.nom}</a>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                <Card className="border-destructive/30">
                  <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-destructive" />Pièges à éviter</CardTitle></CardHeader>
                  <CardContent><ul className="text-sm space-y-1.5 list-disc pl-5">{pb.pieges.map((p, i) => <li key={i}>{p}</li>)}</ul></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-base">Encore à vérifier</CardTitle></CardHeader>
                  <CardContent><ul className="text-sm space-y-1.5 list-disc pl-5 text-muted-foreground">{pb.a_valider.map((p, i) => <li key={i}>{p}</li>)}</ul></CardContent>
                </Card>
              </>
            )}
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
